import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { ExamService } from "../../data-access/exam.service";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { DropdownMenuComponent, MenuItem } from "../../../shared/ui/dropdown-menu/dropdown-menu.component";
import { ActivatedRoute, Router } from "@angular/router";
import { OutlineButtonComponent } from "../../../shared/ui/outline-button/outline-button.component";
import { DropdownSearchComponent } from "../../../shared/ui/dropdown-search/dropdown-search.component";
import { MatSnackBar } from "@angular/material/snack-bar";
import { FormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { FullButtonComponent } from "../../../shared/ui/full-button/full-button.component";
import _ from "lodash";
import { faCaretDown, faChevronLeft, faChevronRight, faX } from "@fortawesome/free-solid-svg-icons";
import { Pagination } from "../exam-submissions/exam-submissions.component";
import { FullLengthPaginationComponent } from "../../../shared/ui/pagination/full-length-pagination/full-length-pagination.component";
import { BreadcrumbComponent, BreadcrumbNavigation } from "../../../shared/ui/breadcrumb/breadcrumb.component";
import { SectionedDropdownComponent } from "src/app/shared/ui/sectioned-dropdown/sectioned-dropdown.component";
import { Subscription } from "rxjs";
import { AuthService } from "../../../login/data-access/auth.service";
import { SegmentedToggleComponent, SegmentedToggleOption } from "../../../shared/ui/segmented-toggle/segmented-toggle.component";
import { SearchInputComponent } from "src/app/shared/ui/search-input/search-input.component";
import { DotSpinnerComponent } from "../../../shared/ui/loaders/dot-spinner.component";
import { instanceStatusBadge } from "../../../shared/utils/instanceStatusBadge";

interface InstanceResponse {
  status: string
  username: string
  family_name: string
  display_name: string
  examinstance_id: number
  exam_uuid: string
  exam_id: number
  user_id: number
  attempt_no: number
  mode: string
  final_score: number
  available_score: number
  assign_time: Date
  start_time: Date
  answer_time: Date
  due_time: Date
  started_late: boolean
  finished_late: boolean
  menuItem: MenuItem[]
  accommodations: any[]
  statusLabel: string
  statusClass: string
}
interface PartInfo {
  name: string
  part_position: number
}
@Component({
  selector: 'exam-all-instances',
  templateUrl: './exam-all-instances.component.html',
  styleUrls: ['../list/list.component.css'],
  imports: [CommonModule, FontAwesomeModule, DropdownMenuComponent, OutlineButtonComponent, FormsModule, MatIconModule, FullButtonComponent, FullLengthPaginationComponent, BreadcrumbComponent, SectionedDropdownComponent, SegmentedToggleComponent, SearchInputComponent, DotSpinnerComponent]
})
export class ExamAllInstancesComponent implements OnInit {
  _snackBar = inject(MatSnackBar)
  route = inject(ActivatedRoute);
  router = inject(Router);
  examService = inject(ExamService)
  examID = -1
  loading = false
  examTitle = ''
  increaseTimeDialog = false
  increaseTimeExamUUID = ''
  increaseTimeMinutes = ''
  changeDueDateDialog = false
  changeDueDateExamUUID = ''
  currentDate = new Date()
  newDueDate = new Date()
  currentPagination: Pagination = {
    limit: 25,
    size: 0,
    page: 0,
    start: 0,
    totalcount: 0,
    maxpage: 0
  }
  modeOptions: SegmentedToggleOption[] = [
    { label: 'All', value: '' },
    { label: 'Standard', value: 'STANDARD' },
    { label: 'Practice', value: 'PRACTICE' },
    { label: 'Preview', value: 'PREVIEW' }
  ]
  mode = ''
  instances: InstanceResponse[] = [];
  resetFullExamDialog = false
  resetFullExamUUID = ''
  partialResetExamDialog = false
  partialResetExamUUID = ''
  partialResetExamPartInfo: PartInfo[] = []
  partialResetExamPartPosition = -1
  increaseTimePartPosition = -1
  confirmNewInstance = false
  newInstanceUsername = ''
  newInstanceExamID = -1
  newExamInstanceOverrideDueDate: Date | null = null
  newExamInstanceMode: string | null = null
  breadcrumbNavs: BreadcrumbNavigation[] = [{
    text: 'Exam List',
    route: 'dashboard/exam/list'
  }]
  searchText = '';

  examModeDropdownItems: any[] = [
    {
      label: 'Standard',
      value: 'STANDARD',
      parentSelectable: false,
    },
    {
      label: 'Practice',
      value: 'PRACTICE',
      parentSelectable: false,
    },
    {
      label: 'Preview',
      value: 'PREVIEW',
      parentSelectable: false,
    },
  ]
  authService = inject(AuthService);
  examAdmin = false


  userPermissionSub: Subscription = this.authService.userDashboard$.subscribe((user) => {
    this.examAdmin = user.result.some(el => el.ui === 'ExamAdmin')
  })
  ngClass: any;

  ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      if (params['exam_id']) {
        this.examID = params['exam_id'];
        this.handleServiceCall()
      }
      if (params['exam_title']) {
        this.examTitle = params['exam_title'];
      }
    })
  }

  handleServiceCall() {
    this.loading = true
    this.examService.getAllExamInstances(this.examID, this.currentPagination.limit, this.currentPagination.page, this.mode, this.searchText).subscribe((response) => {
      if (response.success) {
        this.currentPagination = {
          limit: response.result.limit,
          size: response.result.size,
          page: response.result.page,
          start: response.result.start,
          totalcount: response.result.totalcount,
          maxpage: Math.floor(response.result.totalcount / response.result.limit)
        }
        this.instances = []
        for (let item of response.result.items) {
          const reviewItem: MenuItem = {
            title: 'Review Instance',
            action: () => this.reviewInstance(item)
          }
          let menuItem: MenuItem[] = [reviewItem]
          if (this.examAdmin) {
            // destructive resets kept last, behind a divider, so they're harder to hit by accident
            menuItem = [reviewItem, {
              title: 'View Comments',
              route: 'dashboard/exam/instances/comments',
              queryParams: { queryParams: { exam_uuid: item.exam_uuid } }
            }, {
              title: 'Increase Exam Part Time',
              action: () => {
                this.increaseTimeDialog = true
                this.increaseTimeExamUUID = item.exam_uuid
                this.examService.reviewExamInstance(item.exam_uuid).subscribe((response) => {
                  if (response.success) {
                    this.partialResetExamPartInfo = []
                    for (let part of response.result.parts) {
                      this.partialResetExamPartInfo.push({
                        name: part.details.part_name,
                        part_position: part.part_position
                      })
                    }
                  } else {
                    this._snackBar.open(response.msg, 'Dismiss', { duration: 5000, panelClass: ['error-snackbar'] })
                  }
                })
              }
            }, {
              title: 'Change Due Date',
              action: () => {
                this.changeDueDateDialog = true
                this.changeDueDateExamUUID = item.exam_uuid
              }
            }, {
              title: 'Assign New Instance',
              action: () => {
                this.confirmNewInstance = true
                this.newInstanceUsername = item.username
                this.newInstanceExamID = item.exam_id
              }
            }, {
              title: 'Reset Exam Part',
              danger: true,
              divider: true,
              action: () => {
                this.partialResetExamDialog = true
                this.partialResetExamUUID = item.exam_uuid
                this.examService.reviewExamInstance(item.exam_uuid).subscribe((response) => {
                  if (response.success) {
                    this.partialResetExamPartInfo = []
                    for (let part of response.result.parts) {
                      this.partialResetExamPartInfo.push({
                        name: part.details.part_name,
                        part_position: part.part_position
                      })
                    }
                  } else {
                    this._snackBar.open(response.msg, 'Dismiss', { duration: 5000, panelClass: ['error-snackbar'] })
                  }
                })
                // TODO call exam review or get exam details to get the part positions
              }
            }, {
              title: 'Reset Entire Exam',
              danger: true,
              action: () => {
                this.resetFullExamDialog = true
                this.resetFullExamUUID = item.exam_uuid
              }
            }]
          }
          this.instances.push({ ...item, menuItem, ...instanceStatusBadge(item.status) })
        }
      } else {
        this._snackBar.open(response.msg, 'Dismiss', { duration: 5000, panelClass: ['error-snackbar'] })
      }
      this.loading = false
    })
  }

  reviewInstance(item: InstanceResponse) {
    this.router.navigate(['dashboard/exam/submissions/review'], {
      queryParams: {
        exam_uuid: item.exam_uuid,
        exam_id: item.exam_id,
        exam_title: this.examTitle,
        display_name: item.display_name
      }
    })
  }

  debounceSearch = _.debounce(() => {
    this.handleServiceCall();
  }, 500)

  examList() {
    this.router.navigate(['dashboard/exam/list'])
  }

  cancelIncreaseTimeSave() {
    this.increaseTimeDialog = false
    this.increaseTimeExamUUID = ''
    this.increaseTimeMinutes = ''
    this.partialResetExamPartInfo = []
    this.increaseTimePartPosition = -1
  }

  increaseTimeSave() {
    this.examService.extendExamInstance(this.increaseTimeExamUUID, Number(this.increaseTimeMinutes), this.increaseTimePartPosition).subscribe((response) => {
      if (response.success) {
        this._snackBar.open('Exam Successfully Extended', 'Dismiss', { duration: 3000 })
      } else {
        this._snackBar.open(response.msg, 'Dismiss', { duration: 5000, panelClass: ['error-snackbar'] })
      }
      this.cancelIncreaseTimeSave()
    })
  }

  cancelChangeDueDate() {
    this.changeDueDateDialog = false
    this.changeDueDateExamUUID = ''
  }

  changeDueDateSave() {
    let tempDate = new Date(this.newDueDate)
    // TODO need to format newDueDate to correct timestamp format for API call
    this.examService.newOverallDueDate(this.changeDueDateExamUUID, tempDate.toISOString().split('.')[0] + "Z").subscribe((response) => {
      if (response.success) {
        this._snackBar.open('Due date successfully updated', 'Dismiss', { duration: 3000 })
      } else {
        this._snackBar.open(response.msg, 'Dismiss', { duration: 3000, panelClass: ['error-snackbar'] })
      }
      this.cancelChangeDueDate()
    })
  }

  cancelResetFullExam() {
    this.resetFullExamUUID = ''
    this.resetFullExamDialog = false
  }

  confirmResetFullExam() {
    this.examService.examFullReset(this.resetFullExamUUID).subscribe((response) => {
      if (response.success) {
        this.examService.removeExamInstance(this.resetFullExamUUID).subscribe((response) => {
          if (response.success) {
            this.handleServiceCall()
            this._snackBar.open('Exam Successfully Reset!', 'Dismiss', { duration: 3000 })
            this.cancelResetFullExam()
          } else {
            this._snackBar.open(response.msg, 'Dismiss', { duration: 5000, panelClass: ['error-snackbar'] })
          }
        })
      } else {
        // Don't know if it would be better to keep dialog open or close it
        this._snackBar.open(response.msg, 'Dismiss', { duration: 5000, panelClass: ['error-snackbar'] })
      }
    })
  }

  cancelPartialExamReset() {
    this.partialResetExamDialog = false
    this.partialResetExamUUID = ''
    this.partialResetExamPartInfo = []
    this.partialResetExamPartPosition = -1
  }

  confirmPartialExamReset() {
    if (this.partialResetExamPartPosition !== -1 && this.partialResetExamUUID !== '') {
      this.examService.resetExamPart(this.partialResetExamUUID, this.partialResetExamPartPosition).subscribe((response) => {
        if (response.success) {
          this.handleServiceCall()
          this._snackBar.open('Exam Part Successfully Reset!', 'Dismiss', { duration: 3000 })
          this.cancelPartialExamReset()
        } else {
          this._snackBar.open(response.msg, 'Dismiss', { duration: 5000, panelClass: ['error-snackbar'] })
        }
      })
    }
  }

  cancelNewExamInstance() {
    this.newInstanceUsername = ''
    this.newInstanceExamID = -1
    this.confirmNewInstance = false
  }

  confirmNewExamInstance() {
    if (this.newInstanceExamID !== -1 && this.newInstanceUsername !== null && this.newInstanceUsername !== '') {
      this.examService.assignNewExamInstance(this.newInstanceExamID, this.newInstanceUsername, this.newExamInstanceMode, this.newExamInstanceOverrideDueDate).subscribe((response) => {
        if (response.success) {
          this.handleServiceCall()
          this._snackBar.open('New Exam Instance Successfully Assigned!', 'Dismiss', { duration: 3000 })
          this.cancelNewExamInstance()
          this.confirmResetFullExam()
        } else {
          this._snackBar.open(response.msg, 'Dismiss', { duration: 5000, panelClass: ['error-snackbar'] })
        }
      })
    }
  }

  protected readonly faChevronLeft = faChevronLeft;
  protected readonly faChevronRight = faChevronRight;
  protected readonly faCaretDown = faCaretDown;
  protected readonly faX = faX;
}
